import { loadBusinessCenterOperationalState } from "./businessCenterOperationalStateApi";
import type { BusinessCenterDefaultLandingWorkspaceKey, BusinessCenterUserRecord } from "./types";

export const defaultLandingWorkspaceOptions: Array<{
  key: BusinessCenterDefaultLandingWorkspaceKey;
  label: string;
  route: string;
}> = [
  { key: "business-center-os", label: "Business Center OS Command Center", route: "/admin/business-center-os" },
  { key: "ar-recovery-os", label: "AR Recovery OS", route: "/admin/ar-recovery" },
  { key: "recovery-workbench", label: "Recovery Workbench", route: "/admin/ar-recovery/customer-accounts" },
  { key: "promise-center", label: "Promise Center", route: "/admin/ar-recovery/promise-to-pay" },
  { key: "tickler-center", label: "Tickler Center", route: "/admin/ar-recovery/smart-tickler" },
  { key: "manager-command", label: "Manager Command", route: "/admin/ar-recovery/manager-command-center" },
  { key: "client-portal", label: "Client Portal", route: "/client/portal" },
];

const defaultLandingWorkspaceKeys = new Set(defaultLandingWorkspaceOptions.map((option) => option.key));

type AuthenticatedLandingUser = Pick<BusinessCenterUserRecord, "email" | "role"> & {
  defaultLandingWorkspace?: string;
};

export function normalizeDefaultLandingWorkspace(value: string | null | undefined): BusinessCenterDefaultLandingWorkspaceKey | "" {
  const normalized = String(value || "").trim();
  return defaultLandingWorkspaceKeys.has(normalized as BusinessCenterDefaultLandingWorkspaceKey)
    ? (normalized as BusinessCenterDefaultLandingWorkspaceKey)
    : "";
}

export function getDefaultLandingWorkspaceLabel(value: string | null | undefined) {
  const normalized = normalizeDefaultLandingWorkspace(value);
  return defaultLandingWorkspaceOptions.find((option) => option.key === normalized)?.label || "Role-based fallback";
}

export function getDefaultLandingWorkspaceRoute(value: BusinessCenterDefaultLandingWorkspaceKey) {
  return defaultLandingWorkspaceOptions.find((option) => option.key === value)?.route || "/admin/business-center-os";
}

export function getRoleFallbackLandingWorkspace(role: string | null | undefined): BusinessCenterDefaultLandingWorkspaceKey {
  const normalizedRole = String(role || "").trim().toLowerCase();
  if (normalizedRole === "customer_client_user" || normalizedRole === "client" || normalizedRole === "client_user") {
    return "client-portal";
  }
  if (normalizedRole === "company_team_member" || normalizedRole === "team_member" || normalizedRole === "collector") {
    return "recovery-workbench";
  }
  if (normalizedRole === "company_manager" || normalizedRole === "manager" || normalizedRole === "ar_manager") {
    return "manager-command";
  }
  if (normalizedRole === "company_supervisor" || normalizedRole === "supervisor") {
    return "ar-recovery-os";
  }
  return "business-center-os";
}

export function canAccessDefaultLandingWorkspace(user: AuthenticatedLandingUser, workspace: BusinessCenterDefaultLandingWorkspaceKey) {
  const normalizedRole = String(user.role || "").trim().toLowerCase();
  const isClientUser = normalizedRole === "customer_client_user" || normalizedRole === "client" || normalizedRole === "client_user";
  const isCollector = normalizedRole === "company_team_member" || normalizedRole === "team_member" || normalizedRole === "collector";
  const isManager = normalizedRole === "company_manager" || normalizedRole === "manager" || normalizedRole === "ar_manager";
  const isSupervisor = normalizedRole === "company_supervisor" || normalizedRole === "supervisor";
  const isAdministrator =
    normalizedRole === "kreatethedate_admin" ||
    normalizedRole === "platform_admin" ||
    normalizedRole === "admin" ||
    normalizedRole === "company_admin" ||
    normalizedRole === "tenant_admin" ||
    normalizedRole === "platform_owner";

  if (isClientUser) return workspace === "client-portal";
  if (isAdministrator) return true;
  if (isManager || isSupervisor) return workspace !== "business-center-os" && workspace !== "client-portal";
  if (isCollector) return workspace === "recovery-workbench" || workspace === "promise-center" || workspace === "tickler-center";
  return workspace === "business-center-os";
}

export function resolveDefaultLandingWorkspace(user: AuthenticatedLandingUser): BusinessCenterDefaultLandingWorkspaceKey {
  const requestedWorkspace = normalizeDefaultLandingWorkspace(user.defaultLandingWorkspace);
  if (requestedWorkspace && canAccessDefaultLandingWorkspace(user, requestedWorkspace)) {
    return requestedWorkspace;
  }
  const fallbackWorkspace = getRoleFallbackLandingWorkspace(user.role);
  if (requestedWorkspace && requestedWorkspace !== fallbackWorkspace && typeof console !== "undefined") {
    console.info("Default landing workspace fallback applied.", {
      requestedWorkspace,
      fallbackWorkspace,
      role: user.role || "unknown",
    });
  }
  return fallbackWorkspace;
}

export async function resolveAuthenticatedLandingRoute(sessionUser: AuthenticatedLandingUser) {
  let directoryUser: BusinessCenterUserRecord | null = null;
  const normalizedEmail = sessionUser.email.trim().toLowerCase();

  try {
    const response = await loadBusinessCenterOperationalState<BusinessCenterUserRecord[]>(
      "business_center_company_users",
      { scopeType: "platform" },
    );
    directoryUser = (response.state || []).find((user) => user.email.trim().toLowerCase() === normalizedEmail) || null;
  } catch (error) {
    if (typeof console !== "undefined") {
      console.info("Default landing workspace directory lookup unavailable; using role fallback.", {
        role: sessionUser.role || "unknown",
      });
    }
  }

  const workspace = resolveDefaultLandingWorkspace(directoryUser || sessionUser);
  return getDefaultLandingWorkspaceRoute(workspace);
}
